import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../../styles/AppTheme';
import bg1 from '../../assets/bg1.jpg';
import { yellow } from '../../styles/ThemePrimitives';
import fmb52 from '../../assets/fmb52.png';

const API_BASE = 'https://api.fmb52.com';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  alignSelf: 'center',
  width: '100%',
  maxWidth: 'min(100%, 450px)',
  padding: theme.spacing(2),
  gap: theme.spacing(1),
  margin: '0 auto',
  boxSizing: 'border-box',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  backgroundColor: yellow[50],
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100svh',
  boxSizing: 'border-box',
  padding: theme.spacing(1.5),
  paddingLeft: `max(${theme.spacing(1.5)}, env(safe-area-inset-left))`,
  paddingRight: `max(${theme.spacing(1.5)}, env(safe-area-inset-right))`,
  paddingBottom: `max(${theme.spacing(1.5)}, env(safe-area-inset-bottom))`,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: `url(${bg1})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  fontFamily: theme.typography.fontFamily,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
  },
}));

function formatStatusLabel(status) {
  if (!status) return '—';
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Display date as dd-mm-yyyy (handles ISO YYYY-MM-DD, dd/mm/yyyy, dd-mm-yyyy). */
function formatDateDdMmYyyy(value) {
  if (value == null || value === '') return '—';
  const s = String(value).trim();
  const slashOrDash = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/);
  if (slashOrDash) {
    const [, d, m, y] = slashOrDash;
    const dd = String(d).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const yyyy = y.length === 2 ? `20${y}` : y;
    return `${dd}-${mm}-${yyyy}`;
  }
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const [, y, m, d] = iso;
    return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
  }
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    const dd = String(parsed.getDate()).padStart(2, '0');
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const yyyy = parsed.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
}

export default function ZabihatPublic() {
  const { hashed_id: hashedId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchSchedule = useCallback(async () => {
    if (!hashedId) {
      setError('Invalid link.');
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/wa/zabihat_schedule/${encodeURIComponent(hashedId)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.message || 'Link invalid or expired.');
        setData(null);
        return;
      }
      setData(json?.data ?? json);
    } catch (e) {
      setError('Unable to load schedule. Please try again.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hashedId]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  useEffect(() => {
    document.title = 'Zabihat RSVP - FMB 52';
  }, []);

  const status = (data?.status || '').toLowerCase();
  const isInvited = status === 'invited';

  const handleStatus = async (nextStatus) => {
    if (!hashedId || submitting) return;
    setSubmitting(true);
    setSuccessMessage(null);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/wa/zabihat_schedule/${encodeURIComponent(hashedId)}/status`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: nextStatus }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.message || 'Could not update status.');
        return;
      }
      setSuccessMessage('Your response has been saved.');
      await fetchSchedule();
    } catch (e) {
      setError('Could not update status. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppTheme>
      <CssBaseline />
      <SignInContainer direction="column" alignItems="center" justifyContent="center">
        <Card variant="outlined">
          <Box
            component="img"
            src={fmb52}
            alt="Faiz"
            sx={{
              width: 'auto',
              maxWidth: '100%',
              height: { xs: 96, sm: 130 },
              margin: '0 auto',
              display: 'block',
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: 'brown',
              textAlign: 'center',
              fontSize: { xs: '0.95rem', sm: '1.25rem' },
              lineHeight: 1.35,
              px: { xs: 0.5, sm: 0 },
            }}
          >
            FAIZ-UL-MAWAID-IL-BURHANIYAH
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'brown',
              textAlign: 'center',
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.1rem' },
            }}
          >
            Zabihat
          </Typography>

          <Stack spacing={2} alignItems="center" sx={{ width: '100%', mt: 1, textAlign: 'center' }}>
            {loading && (
              <Box sx={{ py: 4, width: '100%', display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: yellow[400] }} />
              </Box>
            )}

            {!loading && error && (
              <Alert
                severity="error"
                sx={{
                  width: '100%',
                  justifyContent: 'center',
                  '& .MuiAlert-message': { textAlign: 'center', width: '100%' },
                }}
              >
                {error}
              </Alert>
            )}

            {!loading && !error && data && (
              <>
                <Stack spacing={1.5} sx={{ width: '100%', alignItems: 'stretch' }}>
                  <DetailRow label="Name" value={data.name} />
                  <DetailRow label="ITS" value={data.its} />
                  <Box
                    sx={{
                      width: '100%',
                      py: 2,
                      px: 2,
                      borderRadius: 2,
                      border: `2px solid ${yellow[300]}`,
                      bgcolor: yellow[100],
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        display: 'block',
                        color: 'text.secondary',
                        letterSpacing: 1,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                      }}
                    >
                      Scheduled Date &amp; Time
                    </Typography>
                    <Typography
                      variant="h6"
                      component="p"
                      sx={{
                        mt: 1.5,
                        mb: 0.5,
                        fontWeight: 700,
                        color: 'brown',
                        fontSize: { xs: '1.15rem', sm: '1.35rem' },
                      }}
                    >
                      {formatDateDdMmYyyy(data.scheduled_date)}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: 'brown',
                        fontSize: { xs: '1.05rem', sm: '1.15rem' },
                      }}
                    >
                      {data.scheduled_time ?? '—'}
                    </Typography>
                  </Box>
                  <DetailRow
                    label="Zabihat"
                    value={`${data.total_zabihat_done ?? 0} / ${data.total_zabihat_count ?? 0} done`}
                  />
                  {!isInvited && (
                    <DetailRow
                      label="Status"
                      renderValue={
                        <Chip
                          size="small"
                          label={formatStatusLabel(data.status)}
                          color="primary"
                          variant="outlined"
                        />
                      }
                    />
                  )}
                </Stack>

                {isInvited && (
                  <>
                    <Alert
                      severity="info"
                      sx={{
                        width: '100%',
                        mt: 1,
                        justifyContent: 'center',
                        '& .MuiAlert-message': { textAlign: 'center', width: '100%' },
                      }}
                    >
                      No response indicates its wakalat.
                    </Alert>
                    <Stack spacing={1.5} sx={{ width: '100%', mt: 1, maxWidth: '100%' }}>
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={submitting}
                        onClick={() => handleStatus('confirmed')}
                        sx={{
                          fontSize: { xs: '0.95rem', sm: '0.9rem' },
                          minHeight: 48,
                          py: 1.25,
                          backgroundColor: yellow[400],
                          '&:hover': {
                            backgroundColor: yellow[100],
                            color: '#000',
                          },
                        }}
                      >
                        I will come for Zabihat
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        disabled={submitting}
                        onClick={() => handleStatus('wakalat')}
                        sx={{
                          fontSize: { xs: '0.95rem', sm: '0.9rem' },
                          minHeight: 48,
                          py: 1.25,
                          color: yellow[300],
                          borderColor: yellow[300],
                          '&:hover': {
                            backgroundColor: yellow[200],
                            borderColor: '#e0d4b0',
                            color: '#000',
                          },
                        }}
                      >
                        Wakalat
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        disabled={submitting}
                        onClick={() => handleStatus('schedule_later')}
                        sx={{
                          fontSize: { xs: '0.95rem', sm: '0.9rem' },
                          minHeight: 48,
                          py: 1.25,
                          color: yellow[300],
                          borderColor: yellow[300],
                          '&:hover': {
                            backgroundColor: yellow[200],
                            borderColor: '#e0d4b0',
                            color: '#000',
                          },
                        }}
                      >
                        Schedule it for Later
                      </Button>
                    </Stack>
                  </>
                )}

                {successMessage && (
                  <Alert
                    severity="success"
                    sx={{
                      width: '100%',
                      justifyContent: 'center',
                      '& .MuiAlert-message': { textAlign: 'center', width: '100%' },
                    }}
                  >
                    {successMessage}
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
}

function DetailRow({ label, value, renderValue }) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: { xs: 0.5, sm: 0 },
        minHeight: 28,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flexShrink: 0, maxWidth: '42%' }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {renderValue ?? (
          <Typography
            variant="body1"
            sx={{
              wordBreak: 'break-word',
              textAlign: 'right',
              fontSize: { xs: '0.95rem', sm: '1rem' },
            }}
          >
            {value ?? '—'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
